import { gql } from '@apollo/client';
import { FileType, Gender } from '@jbc-year-end-adj/types';

export type QueryResult = {
  clientSetting: ClientSetting;
  request: {
    id: string;
    profile: Profile;
  };
};

type ClientSetting = {
  id: string;
  requestImageUploadSettings: requestImageUploadSettings;
};

type requestImageUploadSettings = {
  handicap: 'none' | 'optional' | 'required';
  nonResidentAndStudyAbroad: 'none' | 'optional' | 'required';
};

export type Profile = {
  otherFamilies: OtherFamily[];
};

export type OtherFamily = {
  id?: string;
  lastName?: string;
  firstName?: string;
  lastNameKana?: string;
  firstNameKana?: string;
  birthday?: string;
  gender?: Gender;
  relationOther?: string;
  income?: Income;
  thisYear?: YearlyInfo;
  nextYear?: YearlyInfo;
  dependentFrom?: string;
  dependentReason?: string;
  diedOn?: string;
};

export type Income = {
  id: number;
  salaryIncome: string;
  salaryEarnings: string;
  businessIncome: string;
  businessExpense: string;
  businessEarnings: string;
  miscellaneousIncomeOfficalPension: string;
  miscellaneousIncomeOther: string;
  miscellaneousExpense: string;
  miscellaneousEarnings: string;
  dividendIncome: string;
  dividendExpense: string;
  dividendEarnings: string;
  realEstateIncome: string;
  realEstateExpense: string;
  realEstateEarnings: string;
  retirementIncome: string;
  lengthOfService?: string;
  retireForDisablity?: boolean;
  isOfficerRetirementAllowance?: boolean;
  retirementIncomeDeductionAmount: string;
  retirementEarnings: string;
  otherIncome: string;
  otherExpense: string;
  otherExpenseSpecialDeduction: string;
  otherEarings: string;
  totalEarnings: string;
  hasSideJob?: boolean;
  mainJobIncome: string;
  sideJobIncome: string;
  hasBusinessIncome?: boolean;
  hasMiscellaneousIncome?: boolean;
  hasDividendIncome?: boolean;
  hasRealEstateIncome?: boolean;
  hasRetirementIncome?: boolean;
  hasOtherIncome?: boolean;
};

type YearlyInfo = {
  id: string;
  postcode0?: string;
  postcode1?: string;
  city?: string;
  street?: string;
  building?: string;
  fullAddress?: string;
  prefecture?: string;
  prefectureId?: string;
  year?: number;
  addressForeign?: string;
  dependentTaxLaw?: boolean;
  incomeAdjustmentDeduction?: boolean;
  income?: string;
  earnings?: string;
  handicapType?: 'none' | 'normal' | 'special';
  handicapDetail?: string;
  handicapImage?: FileType;
  isNonResident?: boolean;
  isStudyAbroad?: boolean;
  relatedToRelativesDocument?: FileType;
  relatedToRemittanceDocument?: FileType;
  provingStudyAbroadDocument?: FileType;
  remittance?: string;
  residenceStatus?: 'same_address' | 'different_address' | 'different_and_foreign_address';
};

const YEARLY_INFO_FRAGMENT = gql`
  fragment YearlyInfo on DependentYearlyInfo {
    id
    residenceStatus
    prefectureId
    postcode0
    postcode1
    city
    street
    building
    addressForeign
    income
    earnings
    isNonResident
    isStudyAbroad
    remittance
    handicapType
    handicapDetail
    handicapImage {
      url
      filename
    }
    relatedToRelativesDocument {
      url
      filename
    }
    relatedToRemittanceDocument {
      url
      filename
    }
    provingStudyAbroadDocument {
      url
      filename
    }
    dependentTaxLaw
    incomeAdjustmentDeduction
  }
`;

export const OTHER_FAMILIES_FRAGMENT = gql`
  fragment OtherFamilies on Profile {
    otherFamilies {
      id
      firstName
      lastName
      firstNameKana
      lastNameKana
      gender
      birthday
      relationOther
      dependentFrom
      dependentReason
      diedOn
      thisYear {
        ...YearlyInfo
      }
      nextYear {
        ...YearlyInfo
      }
      income {
        id
        salaryIncome
        businessIncome
        businessExpense
        miscellaneousIncomeOfficalPension
        miscellaneousIncomeOther
        miscellaneousExpense
        dividendIncome
        dividendExpense
        realEstateIncome
        realEstateExpense
        retirementIncome
        lengthOfService
        retireForDisablity
        isOfficerRetirementAllowance
        otherIncome
        otherExpense
        otherExpenseSpecialDeduction
        otherEarings
        mainJobIncome
        sideJobIncome
        hasSideJob
      }
    }
  }
  ${YEARLY_INFO_FRAGMENT}
`;

export const OTHER_FAMILIES = gql`
  query dependents {
    clientSetting {
      id
      requestImageUploadSettings {
        handicap
        nonResidentAndStudyAbroad
      }
    }
    request {
      id
      profile {
        ...OtherFamilies
      }
    }
  }
  ${OTHER_FAMILIES_FRAGMENT}
`;
